import { Box, Button, Card, CardActions, CardContent, CardHeader, Divider, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import ShipmentBoxesList from '../../../components/ShipmentBoxesList'
import { useTranslation } from 'react-i18next';
import { isString } from 'lodash';
import { useSelector } from 'react-redux';
import { ROLES } from 'src/constants';
import { InfoOutlined } from '@mui/icons-material';
import { useGetWeightTransactionsExistenceQuery } from 'src/_api/mapitShipApi';
import { useMemo } from 'react';

export default function OrderBoxesCard({ name, boxesData, errors, onChange, otoIntegration }) {
    const { t } = useTranslation()
    const { profile } = useSelector((state) => state.entities.profile)

    const shipmentIds = useMemo(() => {
        const ids = []
        if (otoIntegration?.shipment?.id) ids.push(otoIntegration.shipment.id)
        if (otoIntegration?.returnShipment?.id) ids.push(otoIntegration.returnShipment.id)
        return ids
    }, [otoIntegration?.shipment?.id, otoIntegration?.returnShipment?.id])

    const { data: weightTransactions } = useGetWeightTransactionsExistenceQuery(
        { shipmentIds },
        { skip: !shipmentIds.length }
    )

    const hasWeightTransactions = weightTransactions?.some(item => item.hasWeightTransactions)

    const handleChange = ({ target: { value } }) => {
        onChange({ target: { name, value } })
    }

    const addNewBox = () => {
        onChange({ target: { name, value: [...boxesData?.boxes, { dimensions: { length: '', width: '', height: '' }, weight: 1 }] } })
    }

    const isCalculatedWeightDifferent = !!otoIntegration?.calculatedWeight &&
        (Math.floor(Math.max(otoIntegration?.actualWeight, otoIntegration?.volumetricWeight)) !== Math.floor(otoIntegration?.calculatedWeight))

    return (
        <Card>
            <CardHeader
                title={t("Boxes Details")}
                subheader={isString(errors) && errors}
                subheaderTypographyProps={{ color: "error" }}
            />
            <Divider />
            <CardContent>
                <ShipmentBoxesList
                    boxes={boxesData?.boxes ?? []}
                    onChange={handleChange} errors={errors}
                    hideSelectBoxes={profile?.role === ROLES.admin}
                />

                <Grid container spacing={1} sx={{ mt: 1 }}>
                    <Grid item xl={3} lg={3} md={4} sm={6} xs={12} sx={{ display: "flex", alignItems: "center" }}>
                        <Box display="flex" alignItems="center" gap={2}>
                            <Typography variant="h6" color="text.secondary">
                                {t("Boxes Count")}
                            </Typography>
                            {!!boxesData?.packagesCount &&
                                <Typography fontWeight="bold">
                                    {boxesData?.packagesCount}
                                </Typography>}
                        </Box>
                    </Grid>
                    {
                        !otoIntegration &&
                        <Grid item xl={3} lg={3} md={4} sm={6} xs={12} sx={{ display: "flex", alignItems: "center" }}>
                            <Box display="flex" alignItems="center" gap={2}>
                                <Typography variant="h6" color="text.secondary">
                                    {t("Total Weight")}
                                </Typography>
                                {boxesData?.weight &&
                                    <Typography fontWeight="bold">
                                        {`${boxesData?.weight} ${t("kg")}`}
                                    </Typography>
                                }
                            </Box>
                        </Grid>
                    }
                    {
                        !!otoIntegration &&
                        <>
                            <Grid item xl={3} lg={3} md={4} sm={6} xs={12} sx={{ display: "flex", alignItems: "center" }}>
                                <Tooltip title={t("It is the weight that is entered when creating the order.")}>
                                    <IconButton size="small" aria-label="actual-weight-info">
                                        <InfoOutlined color="primary" fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Typography sx={{ mr: 1 }} variant="h6" color="text.lighter" fontWeight="bold">{t("Actual weight")}</Typography>
                                <Typography variant="h6" fontWeight="bold" sx={{ ml: 1 }}>{`${otoIntegration?.actualWeight ?? ''} ${t("kg")}`}</Typography>
                            </Grid>
                            <Grid item xl={3} lg={3} md={4} sm={6} xs={12} sx={{ display: "flex", alignItems: "center" }}>
                                <Tooltip title={t("It is the weight calculated based on the dimensions of the order boxes.")}>
                                    <IconButton size="small" aria-label="volumetric-weight-info">
                                        <InfoOutlined color="primary" fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Typography sx={{ mr: 1 }} variant="h6" color="text.lighter" fontWeight="bold">{t("Volumetric weight")}</Typography>
                                <Typography variant="h6" fontWeight="bold" sx={{ ml: 1 }}>{`${otoIntegration?.volumetricWeight ?? ''} ${t("kg")}`}</Typography>
                            </Grid>
                        </>
                    }
                    {
                        isCalculatedWeightDifferent && <>
                            <Grid item xl={3} lg={3} md={4} sm={6} xs={12} sx={{ display: "flex", alignItems: "center" }}>
                                <Typography sx={{ mr: 2 }} variant="h6" color="text.lighter" fontWeight="bold">{t("Chargeable weight")}</Typography>
                                <Typography variant="h6" fontWeight="bold">{`${otoIntegration?.calculatedWeight} ${t("kg")}`}</Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                    {t("updatedShipmentWeight", { from: Math.max(otoIntegration?.actualWeight, otoIntegration?.volumetricWeight), to: otoIntegration?.calculatedWeight })}
                                    {hasWeightTransactions && ` ${t("deliveryChargesAdjusted")}`}
                                </Typography>
                            </Grid>
                        </>
                    }
                </Grid>
            </CardContent>
            <Divider />
            <CardActions>
                <Box mr="auto" />
                <Button
                    variant="contained"
                    sx={{ textTransform: "none" }}
                    onClick={addNewBox}
                >{t("Add Box")}
                </Button>
            </CardActions>
        </Card>
    );
}
